import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** Swap theme with a circular reveal animation centered on the given
   *  viewport coordinates. Going to dark, the new dark layer grows from
   *  the origin; going to light, the old dark layer shrinks back into
   *  the origin. Falls back to an instant swap when View Transitions is
   *  unsupported or reduced-motion is on. */
  setThemeAnimated: (theme: Theme, x: number, y: number) => void;
}

function applyTheme(theme: Theme) {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark);
  document.documentElement.classList.toggle('dark', isDark);
}

function isDarkAfter(theme: Theme): boolean {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return theme === 'dark' || (theme === 'system' && prefersDark);
}

interface DocumentWithVT {
  startViewTransition?: (cb: () => void) => { ready: Promise<void> };
}

export const useThemeStore = create<ThemeState>()(
  persist(
    set => ({
      theme: 'system',
      setTheme: theme => {
        applyTheme(theme);
        set({ theme });
      },
      setThemeAnimated: (theme, x, y) => {
        const prefersReduced = window.matchMedia(
          '(prefers-reduced-motion: reduce)',
        ).matches;
        const doc = document as unknown as DocumentWithVT;

        if (prefersReduced || typeof doc.startViewTransition !== 'function') {
          applyTheme(theme);
          set({ theme });
          return;
        }

        const root = document.documentElement;
        root.style.setProperty('--theme-reveal-x', `${x}px`);
        root.style.setProperty('--theme-reveal-y', `${y}px`);
        const directionClass = isDarkAfter(theme)
          ? 'theme-to-dark'
          : 'theme-to-light';
        root.classList.add(directionClass);

        const transition = doc.startViewTransition(() => {
          applyTheme(theme);
          set({ theme });
        });
        transition.ready.finally(() => {
          window.setTimeout(() => {
            root.classList.remove(directionClass);
            root.style.removeProperty('--theme-reveal-x');
            root.style.removeProperty('--theme-reveal-y');
          }, 700);
        });
      },
    }),
    { name: 'theme-store' },
  ),
);
